// cspell:ignore datadoghq
import { datadogLogs, Logger } from '@datadog/browser-logs';
import { Injectable } from '@treasury/utils/dependency-injection';
import { WindowService } from '@treasury/utils/services';
import { UserIdentificationData } from '../analytics';
import { BuildType, ConfigurationService } from '../config';

@Injectable()
export class LoggingService {
    constructor(
        private readonly config: ConfigurationService,
        private readonly window: WindowService
    ) {}

    private initialized = false;

    private get console() {
        return this.window.console;
    }

    private get logger() {
        return datadogLogs.logger;
    }

    private get isDev() {
        return this.config.buildType === BuildType.Development;
    }

    public init() {
        if (this.initialized) {
            return;
        }

        const { loggingToken, app, buildType, version } = this.config;
        if (!loggingToken) {
            throw new Error('Could not initialize logging service. No client token provided.');
        }

        datadogLogs.init({
            clientToken: loggingToken,
            site: 'us5.datadoghq.com',
            service: `tm-ux-${app}`,
            env: this.config.environment,
            version,
            forwardErrorsToLogs: buildType === BuildType.Production,
            sessionSampleRate: 100,
            forwardConsoleLogs: ['error'],
        });

        datadogLogs.setGlobalContextProperty('team_name', 'Treasury Management UI');
        datadogLogs.setGlobalContextProperty('business_unit_id', 146700);
        datadogLogs.setGlobalContextProperty('institution_id', this.config.institutionId);
        this.initialized = true;
    }

    public setUserSessionContext(user: UserIdentificationData) {
        datadogLogs.setGlobalContextProperty('user', user);
        datadogLogs.setGlobalContextProperty('user_id', user.userId);
        datadogLogs.setGlobalContextProperty('company_id', user.companyId);
    }

    public logInfo(message: string, context?: object) {
        if (this.isDev) {
            this.console.info(message, context);
        }

        this.guardInit();
        this.logger.info(message, context);
    }

    public logWarning(message: string, context?: object) {
        if (this.isDev) {
            this.console.warn(message, context);
        }

        this.guardInit();
        this.logger.warn(message, context);
    }

    public logError(message: string, e?: Error) {
        if (this.isDev) {
            this.console.error(message, e);
        }

        this.guardInit();
        this.logger.error(message, {}, e);
    }

    private guardInit() {
        if (!this.initialized) {
            throw new Error('LoggingService has not been initialized.');
        }
    }
}
